import { User } from '../../domain/entities/User';

/**
 * Observer Interface
 */
export interface IAuthObserver {
  onUserLogin(user: User): void;
  onUserLogout(): void;
  onUserUpdate(user: User): void;
  onAuthError(error: string): void;
}

/**
 * Observable for Authentication State
 * Follows Observer Pattern
 */
export class AuthObservable {
  private observers: IAuthObserver[] = [];
  private _currentUser: User | null = null;
  private _isAuthenticated: boolean = false;

  get currentUser(): User | null {
    return this._currentUser;
  }

  get isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  subscribe(observer: IAuthObserver): () => void {
    this.observers.push(observer);
    
    // Return unsubscribe function
    return () => {
      const index = this.observers.indexOf(observer);
      if (index > -1) {
        this.observers.splice(index, 1);
      }
    };
  }

  private notifyObservers(event: 'login' | 'logout' | 'update' | 'error', data?: unknown): void {
    this.observers.forEach(observer => {
      try {
        switch (event) {
          case 'login':
            if (data && typeof data === 'object' && 'id' in data) {
              observer.onUserLogin(data as User);
            }
            break;
          case 'logout':
            observer.onUserLogout();
            break;
          case 'update':
            if (data && typeof data === 'object' && 'id' in data) {
              observer.onUserUpdate(data as User);
            }
            break;
          case 'error':
            if (data && typeof data === 'string') {
              observer.onAuthError(data);
            }
            break;
        }
      } catch (error) {
        console.error('Observer notification error:', error);
      }
    });
  }

  setUser(user: User): void {
    this._currentUser = user;
    this._isAuthenticated = true;
    this.notifyObservers('login', user);
  }

  clearUser(): void {
    this._currentUser = null;
    this._isAuthenticated = false;
    this.notifyObservers('logout');
  }

  updateUser(user: User): void {
    this._currentUser = user;
    this.notifyObservers('update', user);
  }

  notifyError(error: string): void {
    this.notifyObservers('error', error);
  }
}
